<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { Highcharts } from '@/utils'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import type { GroupAggStats } from '@/store/stats/types'

type TScores = GroupAggStats['subjectInsights'][0]['scores']

const props = withDefaults(
    defineProps<{
        subjectName?: string
        scores: TScores
    }>(),
    { subjectName: '' }
)

const chartId = `individual-scores-chart__${Math.floor(Math.random() * 1e9)}`
const chart = ref<Highcharts.Chart | null>(null)

const numStudents = computed(() => props.scores.length)

const highestScore = computed(() => {
    if (props.scores.length) {
        return Math.max(...props.scores.map(score => score.scorePct))
    }
    return 0
})

const averageScore = computed(() => {
    if (props.scores.length) {
        return Math.round(
            props.scores.reduce((acc, score) => acc + score.scorePct, 0) / props.scores.length
        )
    }
    return 0
})

const lowestScore = computed(() => {
    if (props.scores.length) {
        return Math.min(...props.scores.map(score => score.scorePct))
    }
    return 0
})

onMounted(() => {
    const scores = props.scores
    const avg = averageScore.value
    chart.value = Highcharts.chart(chartId, {
        chart: {
            type: 'column',
            animation: false,
            backgroundColor: 'rgba(255, 255, 255, 0)',
        },
        title: {
            text: undefined,
        },
        legend: {
            enabled: false,
        },
        credits: {
            enabled: false,
        },
        tooltip: {
            hideDelay: 0,
            outside: true,
            borderWidth: 0,
            borderRadius: 4,
            backgroundColor: '#1F3545',
            padding: 6,
            style: {
                color: '#FFFFFF',
                fontSize: '13px',
            },
            useHTML: true,
            formatter: function () {
                return `
                        <table>
                            <tr>
                                <td style="text-align:center">${this.key}</td>
                            </tr>
                            <tr>
                                <td style="text-align:center">${this.y}%</td>
                            </tr>
                        </table>`
            },
        },
        xAxis: {
            title: {
                text: 'Individual Students',
                style: {
                    fontSize: '11px',
                    color: BrandColors.slate03,
                },
            },
            labels: {
                enabled: false,
            },
            tickLength: 0,
        },
        yAxis: {
            title: {
                text: '',
            },
            opposite: true,
            tickPositions: [ 0, 50, 75, 100 ],
            min: 0,
            max: 100,
            plotLines: [{
                value: avg,
                width: 1,
                dashStyle: 'Dash',
                color: '#1F3545',
            }],
            labels: {
                reserveSpace: true,
                align: 'right',
                format: '{value}%',
                style: {
                    fontSize: '10px',
                },
            },
        },
        series: [{
            type: 'column',
            groupPadding: 0,
            minPointLength: 3,
            maxPointWidth: 50,
            ['borderRadiusTopLeft' as 'borderRadius']: 3,
            ['borderRadiusTopRight' as 'borderWidth']: 3,
            states: {
                hover: {
                    animation: {
                        duration: 5000,
                    },
                    brightness: -0.25,
                },
            },
            data: scores
                .map(score => ({
                    y: score.scorePct,
                    name: score.name,
                    color: score.scorePct < 50 ? '#ff6363' : score.scorePct <= 75 ? '#ffbd47' : '#14ca9e',
                }))
                .sort((a, b) => a.y - b.y),
        }],
    } as Highcharts.Options)
})
</script>

<template>
    <div class="individual-scores-chart">
        <div class="individual-scores-chart__subject-name">
            {{ props.subjectName }}
        </div>
        <div class="individual-scores-chart__data">
            <div class="individual-scores-chart__chart-container">
                <div
                    :id="chartId"
                    class="individual-scores-chart__chart"
                    :class="{
                        'individual-scores-chart__chart--placeholder': numStudents === 0
                    }"
                />
                <div class="individual-scores-chart__y-axis-dots">
                    <div class="individual-scores-chart__y-axis-dot individual-scores-chart__y-axis-dot--green" />
                    <div class="individual-scores-chart__y-axis-dot individual-scores-chart__y-axis-dot--yellow" />
                    <div class="individual-scores-chart__y-axis-dot individual-scores-chart__y-axis-dot--red" />
                </div>
            </div>
            <div class="individual-scores-chart__legend">
                <div class="individual-scores-chart__legend-labels">
                    <div class="individual-scores-chart__legend-label">
                        Highest Individual Score
                    </div>
                    <div class="individual-scores-chart__legend-label">
                        <span class="individual-scores-chart__legend-dash">– -</span>
                        Student Average Score
                    </div>
                    <div class="individual-scores-chart__legend-label">
                        Lowest Individual Score
                    </div>
                </div>
                <div class="individual-scores-chart__legend-values">
                    <div class="individual-scores-chart__legend-value">
                        <span class="individual-scores-chart__legend-value-number">{{ highestScore }}</span>
                        <span class="individual-scores-chart__legend-value-pct">%</span>
                    </div>
                    <div class="individual-scores-chart__legend-value">
                        <span class="individual-scores-chart__legend-value-number">{{ averageScore }}</span>
                        <span class="individual-scores-chart__legend-value-pct">%</span>
                    </div>
                    <div class="individual-scores-chart__legend-value">
                        <span class="individual-scores-chart__legend-value-number">{{ lowestScore }}</span>
                        <span class="individual-scores-chart__legend-value-pct">%</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.individual-scores-chart {
    &__subject-name {
        color: $slate-03;
        font-size: 14px;
        line-height: 19px;
    }

    &__data {
        display: flex;
        margin-left: -10px;
    }

    &__chart-container {
        position: relative;
        width: 62%;
        height: 163px;
    }

    &__chart {
        width: 100%;
        height: 100%;

        &--placeholder {
            background-image: url('@/assets/images/group-distribution-placeholder.svg');
            background-repeat: no-repeat;
            background-position: 14px 2px;
            background-size: 76% 100%;
        }
    }

    &__y-axis-dots {
        display: flex;
        align-items: center;
        flex-direction: column;
        position: absolute;
        top: 0;
        right: 4px;
        width: 12px;
        height: 100%;
    }

    &__y-axis-dot {
        width: 6px;
        height: 6px;
        border-radius: 100%;

        &--green {
            margin-top: 22px;
            background-color: #0ab98f;
        }

        &--yellow {
            margin-top: 24px;
            background-color: $butterscotch;
        }

        &--red {
            margin-top: 38px;
            background-color: $brand-red;
        }
    }

    &__legend {
        display: flex;
        flex-wrap: nowrap;
        padding-top: 12px;
    }

    &__legend-labels {
        margin-left: 38px;
        margin-right: 50px;
        padding-left: 18px;
        color: $slate-01;
        font-size: 12px;
        letter-spacing: 0.22px;
        line-height: 16px;
    }

    &__legend-label {
        position: relative;
        margin: 15px 0;
        white-space: nowrap;
    }

    &__legend-dash {
        position: absolute;
        left: -20px;
    }

    &__legend-values {
        font-size: 18px;
    }

    &__legend-value {
        position: relative;
        margin-bottom: 16px;
        line-height: 15px;
        text-align: right;

        &:first-child {
            margin-top: 14px;
        }
    }

    &__legend-value-number {
        font-weight: 600;
        letter-spacing: -0.3px;
    }

    &__legend-value-pct {
        letter-spacing: 0.48px;
    }
}
</style>
